import { Item, Section } from '@react-stately/collections'
import { Selection } from '@react-types/shared'
import { Input } from 'almanac-ui/TextInputField/Input'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Key, useMemo, useRef, useState } from 'react'
import { KnowledgeBaseSnippet, KnowledgeBaseSnippetKind } from 'types'
import Styles from './SnippetsDropdown.module.scss'
import { SnippetsList } from './SnippetsList'
import { groupSnippetsByKind } from 'hooks/useSnippets'
import { SnippetKindLayout } from 'screens/BrandKits/utils'
import { useFilter } from '@react-aria/i18n'
import { Button } from 'blaze-ui/Button'

interface Props {
  onSelect: (snippet: KnowledgeBaseSnippet) => void
  kinds?: KnowledgeBaseSnippetKind[]
  snippets: KnowledgeBaseSnippet[]
}

const byKinds = (kinds?: KnowledgeBaseSnippetKind[]) => (snippet: KnowledgeBaseSnippet) =>
  !kinds || kinds.includes(snippet.kind)

export const SnippetsDropdown = ({ onSelect, kinds, snippets }: Props) => {
  const [searchTerm, setSearchTerm] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { contains } = useFilter({ sensitivity: 'base' })

  const filteredSnippets = useMemo(() => {
    return snippets
      ?.filter(byKinds(kinds))
      .filter(
        (snippet) => contains(SnippetKindLayout[snippet.kind].title, searchTerm) || contains(snippet.name, searchTerm),
      )
  }, [contains, kinds, searchTerm, snippets])

  if (snippets.length === 0) return null

  const handleSelect = (keys: Selection) => {
    const key = (keys as Set<Key>).values().next().value
    const selectedSnippet = filteredSnippets?.find((snippet) => snippet.id === parseInt(key))
    if (!selectedSnippet) return
    onSelect(selectedSnippet)
  }

  const sections = filteredSnippets ? groupSnippetsByKind(filteredSnippets) : []

  return (
    <DropdownTrigger containFocus={false} popoverClassName={Styles.dropdown} placement="bottom-start">
      <Button variant="ghost" aria-label="more" size="xs">
        More...
      </Button>
      {(close) => (
        <div ref={dropdownRef}>
          <div className={Styles.header}>
            <Input
              onChange={(e) => setSearchTerm(e.target.value)}
              ref={searchRef}
              value={searchTerm}
              name="search"
              className={Styles.searchInput}
              placeholder="Search by name or type..."
            />
          </div>
          <div className={Styles.divider}>
            <div />
          </div>
          <div className={Styles.options}>
            {(filteredSnippets?.length || 0) > 0 ? (
              <SnippetsList
                items={sections}
                inputRef={searchRef}
                dropdownRef={dropdownRef}
                onSelectionChange={(keys) => {
                  handleSelect(keys)
                  close()
                }}
              >
                {(item) => (
                  <Section key={item.name} items={item.children} title={item.name}>
                    {(item) => (
                      <Item textValue={item.name} key={item.id}>
                        <span className={Styles.snippetName}>{item.name}</span>
                      </Item>
                    )}
                  </Section>
                )}
              </SnippetsList>
            ) : (
              <div className={Styles.emptyState}>
                {searchTerm.length > 0
                  ? 'Nothing found... Try a different search'
                  : 'No snippets available... Try creating one'}
              </div>
            )}
          </div>
        </div>
      )}
    </DropdownTrigger>
  )
}
